/**
 * Head sampling.
 *
 * Storage is a single-node ClickHouse on a 50Gi volume with 14-day retention;
 * the ~90 spans/sec sustained ceiling is an *estimate*, not a measurement.
 * Head sampling is therefore the safety valve that keeps a traffic spike from
 * filling the volume, and it lives on the Worker so unsampled traces cost no
 * egress and no collector CPU at all.
 */

/**
 * Deterministic per-trace sampling decision.
 *
 * Derived from the trace id rather than Math.random() so every service that
 * sees the same trace makes the same decision. A random draw per hop would
 * produce traces sampled in one service and missing in the next, which is
 * worse than not sampling at all.
 */
export function shouldSample(traceId: string, rate: number): boolean {
	if (rate >= 1) return true;
	if (rate <= 0) return false;
	if (traceId.length < 8) return false;

	// Low 4 bytes of the trace id as a uint32, normalised to [0, 1).
	const value = parseInt(traceId.slice(-8), 16);
	if (!Number.isFinite(value)) return false;
	return value / 0x1_0000_0000 < rate;
}

/**
 * Parse a sample-rate binding. Workers deliver `vars` as strings, and an
 * unset or malformed value must fail closed to 0 (tracing off) rather than
 * defaulting to full-rate ingestion into a storage-constrained backend.
 */
export function parseSampleRate(raw: string | number | undefined | null, fallback = 0): number {
	if (raw === undefined || raw === null || raw === '') return fallback;
	const value = typeof raw === 'number' ? raw : Number.parseFloat(raw);
	if (!Number.isFinite(value)) return fallback;
	return Math.min(Math.max(value, 0), 1);
}
